import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, ArrowUp, MapPin, Facebook } from 'lucide-react';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useCharterCompanies } from '../hooks/useCharterCompanies';
import { FOOTER_MENUS, ROUTES } from '../data/stjSite';

/**
 * Public footer - mirrors the reservestj.com footer block for block:
 *
 *   col 1 + 2  EXCURSION COMPANIES (alphabetical, split in two)   <- top of the footer
 *   col 3      ISLAND DIRECTORY / RESERVE ST JOHN / ONLINE SHOP
 *   col 4      brand + contact, CONCIERGE SERVICES, SPECIAL EXCURSIONS
 *
 * The company list is driven entirely by Admin -> Tours & Charters and links each company
 * on its legacy (already indexed) URL when it has one. Every other link comes from
 * FOOTER_MENUS in data/stjSite.js, which holds the legacy URLs one-for-one.
 */
const FooterLink = ({ href, label, testId }) => {
  const external = /^https?:\/\//i.test(href) || /^(mailto|tel):/i.test(href);
  const className = 'text-slate-600 hover:text-blue-700 transition-colors text-sm';
  if (external || href.includes('#')) {
    return (
      <a
        href={href}
        {...(/^https?:\/\//i.test(href) ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
        className={className}
        data-testid={testId}
      >
        {label}
      </a>
    );
  }
  return <Link to={href} className={className} data-testid={testId}>{label}</Link>;
};

const LinkColumn = ({ title, links, testId }) => (
  <div data-testid={testId}>
    <h4 className="text-slate-900 font-semibold mb-4 tracking-wide">{title}</h4>
    <ul className="space-y-2">
      {links.map((link) => (
        <li key={link.label}>
          <FooterLink href={link.href} label={link.label} />
        </li>
      ))}
    </ul>
  </div>
);

const Footer = () => {
  const { siteName, siteTagline, logoUrl, supportEmail, sitePhone } = useSiteSettings();
  const { companies } = useCharterCompanies();
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const telHref = sitePhone ? `tel:${String(sitePhone).replace(/[^0-9+]/g, '')}` : null;

  const half = Math.ceil(companies.length / 2);
  const companyColumns = companies.length ? [companies.slice(0, half), companies.slice(half)] : [];

  return (
    <footer className="bg-slate-50 border-t border-slate-200" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-4">
          {/* ---------- EXCURSION COMPANIES (two columns, first in the footer) ---------- */}
          {companyColumns.map((group, idx) => (
            <div key={`companies-${idx}`} data-testid={idx === 0 ? 'footer-excursion-companies' : `footer-excursion-companies-${idx}`}>
              <h4 className="text-slate-900 font-semibold mb-4 tracking-wide">EXCURSION COMPANIES</h4>
              <ul className="space-y-2">
                {group.map((company) => (
                  <li key={company.slug}>
                    <Link
                      to={`/${company.public_slug || company.slug}`}
                      className="text-slate-600 hover:text-blue-700 transition-colors text-sm"
                      data-testid={`footer-company-${company.slug}`}
                    >
                      {company.name}
                    </Link>
                  </li>
                ))}
              </ul>
              {idx === 1 && (
                <Link
                  to={ROUTES.liveWebcams}
                  className="mt-6 inline-flex items-center gap-2 rounded-lg border border-blue-200 bg-white px-4 py-3 text-sm font-bold uppercase tracking-wide text-blue-700 hover:bg-blue-50"
                  data-testid="footer-webcams-cta"
                >
                  Live Island Streaming Webcams
                </Link>
              )}
            </div>
          ))}

          {/* ---------- ISLAND DIRECTORY / RESERVE ST JOHN / ONLINE SHOP ---------- */}
          <div className="space-y-8">
            <LinkColumn title="ISLAND DIRECTORY" links={FOOTER_MENUS.islandDirectory} testId="footer-island-directory" />
            <LinkColumn title="RESERVE ST JOHN" links={FOOTER_MENUS.reserveStJohn} testId="footer-reserve-st-john" />
            <LinkColumn title="ONLINE SHOP" links={FOOTER_MENUS.onlineShop} testId="footer-online-shop" />
          </div>

          {/* ---------- BRAND / CONTACT + CONCIERGE + SPECIAL EXCURSIONS ---------- */}
          <div className="space-y-8">
            <div>
              <Link to="/" className="inline-block mb-4">
                {logoUrl ? (
                  <img src={logoUrl} alt={siteName || 'Site logo'} className="h-12" />
                ) : (
                  <span className="text-2xl font-bold text-slate-900">{siteName}</span>
                )}
              </Link>
              {siteTagline && <p className="text-slate-600 text-sm mb-4">{siteTagline}</p>}

              <div className="space-y-2">
                {supportEmail && (
                  <a href={`mailto:${supportEmail}`} className="flex items-center text-slate-600 hover:text-blue-700 transition-colors text-sm" data-testid="footer-email">
                    <Mail className="w-4 h-4 mr-2 text-blue-600" />
                    {supportEmail}
                  </a>
                )}
                <p className="flex items-start text-slate-600 text-sm" data-testid="footer-address">
                  <MapPin className="w-4 h-4 mr-2 mt-0.5 shrink-0 text-blue-600" />
                  P.O. Box 1797 St John VI 00831
                </p>
                {telHref && (
                  <a href={telHref} className="flex items-center text-slate-600 hover:text-blue-700 transition-colors text-sm" data-testid="footer-phone">
                    <Phone className="w-4 h-4 mr-2 text-blue-600" />
                    {sitePhone}
                    <span className="ml-2 text-xs text-slate-500">Txt or Call Help Line</span>
                  </a>
                )}
                <a
                  href={ROUTES.facebookGroup}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center text-slate-600 hover:text-blue-700 transition-colors text-sm"
                  data-testid="footer-facebook"
                >
                  <Facebook className="w-4 h-4 mr-2 text-blue-600" /> Facebook Group
                </a>
              </div>
            </div>

            <LinkColumn title="CONCIERGE SERVICES" links={FOOTER_MENUS.conciergeServices} testId="footer-concierge-services" />
            <LinkColumn title="SPECIAL EXCURSIONS" links={FOOTER_MENUS.specialExcursions} testId="footer-special-excursions" />
            <LinkColumn title="LEGAL" links={FOOTER_MENUS.legal} testId="footer-legal" />
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-slate-500 text-sm">
              &copy; {currentYear} {siteName}. All rights reserved.
            </p>

            <button
              onClick={scrollToTop}
              className="mt-4 md:mt-0 flex items-center text-slate-600 hover:text-blue-700 transition-colors text-sm"
              data-testid="back-to-top"
            >
              Back to top
              <ArrowUp className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
